import React, { FC, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { transactionService } from "../../../store/actions";
import useCommon from "../../../common/hooks/useCommon";
import {
  baseUnit,
  calculateTransactionFeeSavings,
  centerTruncate,
  formatNumber,
  formatPercent,
  formatUnit,
  getConfirmationDuration,
  getPlural,
  getTimeLang,
  hexToUtf8,
  toGwei,
  toXDCNumber,
} from "../../../common/utils/function";
import BPrice from "../../common/b-price";
import BTooltip from "../../../components/b-tooltip";
import TxStatus from "../../common/tx-status";
import { useSelector } from "react-redux";
import BSkeleton from "../../../components/b-skeleton";
import BCopy from "../../../components/b-copy";
import { useTranslation } from "react-i18next";
import TokenTransfers from "./common/token-transfers";
import TransactionAction from "./common/transaction-action";
import AddressName from "../../common/address-name";
import BIdenticons from "../../../components/b-identicon";
import TokenImage from "../../common/token-image";

interface TransactionDetailsData {
  actions: any;
  timestamp: any;
  block: any;
  confirmation_duration: any;
  confirmations: any;
  created_contract: any;
  from: any;
  to: any;
  value: any;
  gas_limit: any;
  gas_used: any;
  gas_price: any;
  base_fee_per_gas: any;
  max_fee_per_gas: any;
  max_priority_fee_per_gas: any;
  tx_burnt_fee: any;
  nonce: number;
  input: string;
  raw_input: string;
  decoded_input: any;
  inputData: string;
  type: number;
  position: number;
  index: number;
  status: any;
  revert_reason: any;
  method: string;
  token_transfers: any;
  xrc20Transfers: any;
  xrc404Transfers: any;
  xrc721Transfers: any;
  xrc1155Transfers: any;
  tokenBurnt: any;
  tokenMint: any;
  fiat_value: any;
  exchange_rate: any;
  block_timestamp: any;
  last_seen: any;
  // decoded_input_data: any;
}

const TransactionDetailsOverview: FC<any> = (props) => {
  const { hash } = useParams();
  const { getTime, getXDC, get0x } = useCommon();
  const priceUsd = useSelector((e: Object) => (e as any)?.common?.stats);
  const _tokenType = useSelector((e: Object) => (e as any)?.common?.tokenType);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(windowWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [isGettingError, setIsGettingError] = useState(false);
  const { t, i18n } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const [decodeType, setDecodeType] = useState("default");
  const [selectedInputType, setSelectedInputType] = useState<string>("hex");
  const [summary, setSummary] = useState<any>({});
  const [internalTxn, setInternalTxn] = useState<any>([]);
  const [stats, setStats] = useState<TransactionDetailsData>({
    actions: [],
    timestamp: "",
    block: "",
    confirmations: "",
    confirmation_duration: [],
    created_contract: {},
    from: {},
    to: {},
    value: "",
    gas_limit: 0,
    gas_used: 0,
    gas_price: "",
    base_fee_per_gas: "",
    max_fee_per_gas: "",
    max_priority_fee_per_gas: "",
    tx_burnt_fee: "",
    nonce: 0,
    input: "",
    raw_input: "",
    decoded_input: {},
    inputData: "",
    type: 0,
    position: 0,
    index: 0,
    status: null,
    revert_reason: {},
    method: "",
    token_transfers: [],
    xrc20Transfers: [],
    xrc404Transfers: [],
    xrc721Transfers: [],
    xrc1155Transfers: [],
    tokenBurnt: [],
    tokenMint: [],
    fiat_value: 0,
    exchange_rate: 0,
    block_timestamp: "",
    last_seen: "",
    // decoded_input_data: any;
  });
  const getTransaction = async () => {
    try {
      setLoading(true);
      transactionService
        .getTransactionSummary(hash)
        .then((response) => {
          setSummary(response?.data);
        })
        .catch((e) => setIsGettingError(true));

      transactionService
        .getInternalTransaction(hash, "")
        .then((response) => {
          setInternalTxn(
            response?.items?.filter(
              (e: any) => Number(e.value) > 0 && e.type === "call"
            ) ?? []
          );
        })
        .catch((e) => setIsGettingError(true));

      transactionService
        .getTransaction(hash)
        .then((response) => {
          response.xrc20Transfers =
            response?.token_transfers.filter(
              (e: any) =>
                !/^0x0+$/.test(e?.to.hash) &&
                !/^0x0+$/.test(e?.from.hash) &&
                e?.token.type === "ERC-20"
            ) ?? [];
          response.xrc404Transfers =
            response?.token_transfers.filter(
              (e: any) =>
                !/^0x0+$/.test(e?.to.hash) &&
                !/^0x0+$/.test(e?.from.hash) &&
                e?.token.type === "ERC-404"
            ) ?? [];
          response.xrc721Transfers =
            response?.token_transfers.filter(
              (e: any) =>
                !/^0x0+$/.test(e?.to.hash) &&
                !/^0x0+$/.test(e?.from.hash) &&
                e?.token.type === "ERC-721"
            ) ?? [];
          response.xrc1155Transfers =
            response?.token_transfers.filter(
              (e: any) =>
                !/^0x0+$/.test(e?.to.hash) &&
                !/^0x0+$/.test(e?.from.hash) &&
                e?.token.type === "ERC-1155"
            ) ?? [];
          response.tokenBurnt =
            response?.token_transfers.filter((e: any) =>
              /^0x0+$/.test(e?.to.hash)
            ) ?? [];
          response.tokenMint =
            response?.token_transfers.filter((e: any) =>
              /^0x0+$/.test(e?.from.hash)
            ) ?? [];
          props.setTabView((prevState: any) => ({
            ...prevState,
            tokenTransfers: !!response?.token_transfers?.length,
          }));
          setStats(response);
          setLoading(false);
        })
        .catch((e) => setIsGettingError(true));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getTransaction();
  }, [hash]);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isGettingError) {
      props?.handleGetError(true);
    }
  }, [isGettingError]);

  const handleSelect = (event: any) => {
    const { value } = event.target;
    setSelectedInputType(value);
  };

  return (
    <React.Fragment>
      {" "}
      <div className="bs-card mb-3">
        <div className="bs-card-body py-3 px-4">
          <div className="d-flex align-items-center gap-2">
            <span className="me-2 text-muted">
              <i className="bi bi-caret-right-fill"></i> Call
            </span>
            {stats?.actions?.method_id && (
              <span className="badge bg-light border border-secondary text-dark fw-monospace px-2 me-2">
                {`0x${stats.actions.method_id}`}
              </span>
            )}
            {stats?.actions?.method_call && (
              <span className="fw-medium text-dark">
                {stats.actions.method_call}
              </span>
            )}

            <span>By</span>
            {loading ? <BSkeleton /> : <>{stats?.from?.hash}</> }
            
          </div>
        </div>
      </div>
      <div className="bs-card mb-3">
        <div className="bs-card-body py-3 px-4">
          <div className="bs-list">
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={t(
                    "A TxHash or transaction hash is a unique 66-character identifier that is generated whenever a transaction is executed."
                  )}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Transaction Hash")}:
              </div>
              <div className="bs-list-content">
                <span className={`${!stats?.block ? "text-secondary" : ""}`}>
                  {get0x(hash)}
                </span>
                <BCopy
                  value={get0x(hash)}
                  tooltip={`${t("TxHash to clipboard")}`}
                />
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip text={t("The status of the transaction.")}>
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {stats?.method ? t("Status and method") : t("Status")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {stats?.block ? (
                      <>
                        <TxStatus status={stats?.status} />{" "}
                        {stats?.method !== "0xundefined" && (
                          <span className="ms-2 badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap px-2 me-2">
                            {stats?.actions.method_call}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-muted fw-medium text-start text-wrap py-1.5 px-2 me-1">
                        <i className="bi bi-record-circle-fill me-1"></i>Pending
                      </span>
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>
            {loading ? (
              <BSkeleton />
            ) : (
              stats?.revert_reason && (
                <div className="bs-list-row">
                  <div className="bs-list-title">
                    <BTooltip text={t("The revert reason of the transaction.")}>
                      <i className="bi bi-question-circle me-2"></i>
                    </BTooltip>
                    {t("Revert Reason")}:
                  </div>
                  <div className="bs-list-content">
                    <div className="bs-list-input-data">
                      <div className="bs-input-data-title">
                        {t("Method id")}
                      </div>
                      <div className="ml-10">
                        {stats?.revert_reason?.method_id}
                      </div>
                    </div>
                    <hr />
                    <div className="bs-list-input-data">
                      <div className="bs-input-data-title">{t("Call")}</div>
                      <div>{stats?.revert_reason?.method_call}</div>
                    </div>
                    {stats?.revert_reason?.parameters?.length > 0 && (
                      <React.Fragment>
                        <div className="bs-log-table">
                          <div className="bs-log-header">
                            <div className="bs-log-table-cell bs-log-tbl-row">
                              {t("Name")}
                            </div>
                            <div className="bs-log-table-cell bs-log-tbl-row">
                              {t("Type")}
                            </div>
                            <div className="bs-log-table-cell-data">
                              {t("Data")}
                            </div>
                          </div>

                          <div className="bs-list">
                            {stats?.revert_reason?.parameters?.map(
                              (para: any, paraindex: number) => (
                                <React.Fragment key={paraindex}>
                                  <div
                                    className={
                                      paraindex <
                                      stats?.revert_reason?.parameters?.length -
                                        1
                                        ? "bs-log-table-cell bs-log-tbl-row text-truncate"
                                        : "bs-log-table-cell text-truncate"
                                    }
                                  >
                                    {para?.name}
                                  </div>
                                  <div
                                    className={
                                      paraindex <
                                      stats?.revert_reason?.parameters?.length -
                                        1
                                        ? "bs-log-table-cell bs-log-tbl-row text-truncate"
                                        : "bs-log-table-cell text-truncate"
                                    }
                                  >
                                    {para?.type}
                                  </div>
                                  <div className="bs-log-table-cell-data d-flex">
                                    {para?.type === "address" && (
                                      <div className="me-2">
                                        <BIdenticons
                                          seed={getXDC(para?.value)}
                                          size={8}
                                          scale={3}
                                          color="#3498db"
                                          bgColor="#d35400"
                                        />
                                      </div>
                                    )}
                                    <div className="mt-1">
                                      <BTooltip text={para?.value}>
                                        {para?.type === "address" ? (
                                          <React.Fragment>
                                            <Link
                                              to={`/address/${get0x(
                                                para?.value
                                              )}`}
                                            >
                                              {isMobile
                                                ? centerTruncate(para?.value)
                                                : para?.value}
                                            </Link>
                                          </React.Fragment>
                                        ) : isMobile ? (
                                          centerTruncate(para?.value)
                                        ) : (
                                          para?.value
                                        )}
                                        {para?.type === "address" && (
                                          <BCopy
                                            value={para?.value}
                                            tooltip="Logdata to clipboard"
                                          />
                                        )}
                                      </BTooltip>
                                    </div>
                                  </div>
                                </React.Fragment>
                              )
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              )
            )}
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={t(
                    "Number of the block in which the transaction is recorded. Block confirmations indicate how many blocks have been added since the transaction was produced."
                  )}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Block")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {stats?.block ? (
                      <React.Fragment>
                        <Link to={`/block/${stats?.block}`}>
                          {stats?.block}
                        </Link>
                        &nbsp;
                        <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                          {getPlural("Block", stats?.confirmations)}{" "}
                          <span> {t("Confirmation")}</span>
                        </span>
                      </React.Fragment>
                    ) : (
                      "(Pending)"
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={t(
                    "The date and time at which a transaction is produced."
                  )}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Timestamp")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {stats?.block ? (
                      <React.Fragment>
                        <i className="bi bi-clock"></i> &nbsp;
                        {getTimeLang(stats.block_timestamp, t)} &nbsp;
                        {getTime(
                          stats.timestamp,
                          "(MMM-DD-YYYY hh:mm:ss A Z)",
                          false
                        )}
                        {" | "}
                        <span className="text-muted">
                          <BTooltip
                            text={t(
                              "Estimated duration between First Seen and included in block"
                            )}
                          >
                            {t("Confirmed within")}{" "}
                            {getConfirmationDuration(
                              stats.confirmation_duration
                            )}
                          </BTooltip>
                        </span>
                      </React.Fragment>
                    ) : (
                      "(Pending)"
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>
            {stats.block === null ? (
              <div className="bs-list-row">
                <div className="bs-list-title">
                  <BTooltip
                    text={t(
                      "The date and time at which a transaction is produced."
                    )}
                  >
                    <i className="bi bi-question-circle me-2"></i>
                  </BTooltip>
                  {t("Last Seen")}:
                </div>
                <div className="bs-list-content">
                  {loading ? (
                    <BSkeleton />
                  ) : (
                    <React.Fragment>
                      {stats?.block === null ? (
                        <React.Fragment>
                          <i className="bi bi-clock"></i> &nbsp;
                          {getTimeLang(stats.last_seen, t)} &nbsp;
                          {getTime(
                            stats.last_seen,
                            "(MMM-DD-YYYY hh:mm:ss A Z)",
                            false
                          )}
                        </React.Fragment>
                      ) : (
                        "(Pending)"
                      )}
                    </React.Fragment>
                  )}
                </div>
              </div>
            ) : null}
            <hr />
            {stats?.actions?.method_call && (
              <React.Fragment>
                <div className="bs-list-row">
                  <div className="bs-list-title">
                    <BTooltip
                      text={t("Highlighted events of the transaction.")}
                    >
                      <i className="bi bi-lightning-charge-fill bs-font-primary me-2"></i>
                    </BTooltip>
                    {t("Transaction Action")}:
                  </div>
                  <div className="bs-list-content">
                    {loading ? (
                      <BSkeleton />
                    ) : (
                      <React.Fragment>
                        {stats?.actions && (
                          <p className="d-flex align-items-center">
                            <span className="me-1 text-muted">
                              <i className="bi bi-caret-right-fill"></i> Call
                            </span>
                            <span className="me-1">
                              {" "}
                              {stats?.actions?.method_call?.split("(")[0]}
                            </span>
                            <span className="me-1 text-muted">
                              Function
                              {String(stats?.from?.is_contract) === "true" && (
                                <BTooltip text="Contract">
                                  <i className="bi bi-file-earmark-text me-1 gray"></i>
                                </BTooltip>
                              )}
                              <Link to={`/address/${get0x(stats?.from?.hash)}`}>
                                {centerTruncate(getXDC(stats?.from?.hash))}
                              </Link>
                              &nbsp; on
                              {String(stats?.to?.is_contract) === "true" && (
                                <BTooltip text="Contract">
                                  <i className="bi bi-file-earmark-text me-1 gray"></i>
                                </BTooltip>
                              )}
                              <Link to={`/address/${get0x(stats?.to?.hash)}`}>
                                {centerTruncate(getXDC(stats?.to?.hash))}
                              </Link>
                            </span>
                          </p>
                        )}
                      </React.Fragment>
                    )}
                  </div>
                </div>
                <hr />
              </React.Fragment>
            )}
            <TransactionAction
              list={summary?.summaries}
              loading={loading}
              transaction={stats}
            />
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip text={t("The sending party of the transaction.")}>
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("From")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {stats?.from?.is_contract && (
                      <BTooltip text={t("Contract")}>
                        <i className="bi bi-file-text me-1 gray"></i>
                      </BTooltip>
                    )}
                    <Link to={`/address/${get0x(stats?.from?.hash)}`}>
                      {getXDC(stats?.from?.hash)}
                    </Link>
                    &nbsp; {stats?.from?.name && `(${stats?.from?.name})`}
                    <BCopy value={get0x(stats?.from?.hash)} />
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={t(
                    "The receiving party of the transaction (could be a contract address)."
                  )}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {stats?.to?.is_contract || stats?.to?.is_verified
                  ? "Interacted With (To):"
                  : "To:"}
                {/* {stats?.token_transfers && (
        <span className="ms-1">({stats?.token_transfers?.token.symbol}: {stats?.token_transfers?.token.name})</span>
    )} */}
              </div>

              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {stats?.to?.is_contract && (
                      <BTooltip text={t("Contract")}>
                        <i className="bi bi-file-text me-1 gray"></i>
                      </BTooltip>
                    )}
                    {stats?.to ? (
                      <React.Fragment>
                        <Link to={`/address/${get0x(stats?.to?.hash)}`}>
                          {getXDC(stats?.to?.hash)}
                        </Link>
                        &nbsp; {stats?.to?.name && `(${stats?.to?.name})`}
                        <BCopy value={get0x(stats?.to?.hash)} />
                      </React.Fragment>
                    ) : (
                      <span>
                        [
                        {String(stats?.created_contract?.is_contract) ===
                          "true" && (
                          <BTooltip text="Contract">
                            <i className="bi bi-file-earmark-text me-1 gray"></i>
                          </BTooltip>
                        )}
                        {stats?.created_contract?.is_contract && (
                          <React.Fragment>
                            <BTooltip text={`${stats?.created_contract?.hash}`}>
                              <Link
                                to={`/address/${get0x(
                                  stats?.created_contract?.hash
                                )}`}
                              >
                                {stats?.created_contract?.name ||
                                  stats?.created_contract?.hash}
                              </Link>
                            </BTooltip>
                          </React.Fragment>
                        )}
                        &nbsp; {t("Created")}]
                        <BCopy
                          value={get0x(stats?.created_contract?.hash)}
                          tooltip="Address"
                        />
                      </span>
                    )}

                    {(stats?.to?.is_verified ||
                      stats?.created_contract?.is_verified) && (
                      <BTooltip text={t("Contract Execution Completed")}>
                        <i className="bi bi-check-circle-fill bs_verified-contract mx-2"></i>
                      </BTooltip>
                    )}

                    <div className="d-flex flex-column small gap-1 mt-2">
                      <div className="d-flex flex-column px-1 mx-n1 py-1 my-n1">
                        <div
                          className="custom-scrollbar"
                          style={{ maxHeight: "200px", overflowY: "auto" }}
                        >
                          {internalTxn?.map((e: any, i: number) => (
                            <div
                              className="d-flex flex-wrap align-items-center gap-1"
                              key={i}
                            >
                              <p>
                                <i className="bi bi-arrow-return-right"></i>{" "}
                                Transfer {toXDCNumber(e?.value, 18)}{" "}
                                {baseUnit()} from{" "}
                                <Link to={`/address/${get0x(e?.from?.hash)}`}>
                                  <AddressName data={e?.from} />
                                </Link>{" "}
                                to{" "}
                                <Link to={`/address/${get0x(e?.to?.hash)}`}>
                                  <AddressName data={e?.to} />
                                </Link>
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>
            <hr />

            <TokenTransfers
              list={stats?.xrc20Transfers}
              label={`${_tokenType}-20 ${t("Tokens Transferred")}`}
              transferType={`${_tokenType}-20`}
            />
            <TokenTransfers
              list={stats?.xrc404Transfers}
              label={`${_tokenType}-404 ${t("Tokens Transferred")}`}
              transferType={`${_tokenType}-404`}
            />
            <TokenTransfers
              list={stats?.xrc721Transfers}
              label={`${_tokenType}-721 ${t("Tokens Transferred")}`}
              transferType={`${_tokenType}-721`}
            />
            <TokenTransfers
              list={stats?.xrc1155Transfers}
              label={`${_tokenType}-1155 ${t("Tokens Transferred")}`}
              transferType={`${_tokenType}-1155`}
            />
            <TokenTransfers
              list={stats?.tokenMint}
              label="Tokens minted"
              transferType="Mint"
            />
            <TokenTransfers
              list={stats?.tokenBurnt}
              label="Tokens burnt"
              transferType="Burnt"
            />

            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={t(
                    "The value being transacted in Ether and fiat value. Note: You can click the fiat value (if available) to see historical value at the time of transaction."
                  )}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Value")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    <TokenImage
                      image={priceUsd?.coin_image}
                      symbol={process.env.REACT_APP_BASE_UNIT}
                      params={{ width: "16px", className: "me-1" }}
                    />
                    {formatUnit(toXDCNumber(stats?.value, 18, 6))} &nbsp;
                    <BPrice
                      value={stats?.value}
                      coingeckoID={process.env.REACT_APP_COINGECKO_ID}
                      timestamp={stats?.timestamp}
                      XDCUSD={priceUsd?.coin_price}
                      type=""
                      decimal={18}
                    />
                    &nbsp;{" "}
                    {String(stats?.status) === "error" && (
                      <span className="text-warning">[- CANCELLED]</span>
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={t(
                    "Amount paid to process the transaction in Ether and fiat value."
                  )}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Actual Tx Cost/Fee")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {stats?.block ? (
                      <React.Fragment>
                        {formatUnit(
                          toXDCNumber(stats.gas_price * stats.gas_used, 18, 6)
                        )}
                        &nbsp;
                        <BPrice
                          value={stats.gas_price * stats.gas_used}
                          coingeckoID={process.env.REACT_APP_COINGECKO_ID}
                          timestamp={stats.timestamp}
                          XDCUSD={priceUsd?.coin_price}
                          type=""
                          decimal={18}
                        />
                      </React.Fragment>
                    ) : (
                      "(Pending)"
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip text={t("Gas Used Percentage")}>
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Gas usage & limit by txn")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    <div className="flex">
                      <strong>{stats?.gas_used}</strong> |{" "}
                      <strong>{stats?.gas_limit}</strong>
                      <div className="flex items-center gap-2">
                        <div className="gas-progress-bar">
                          <div
                            className="progress"
                            style={{
                              width: `${
                                (stats?.gas_used / stats?.gas_limit) * 100
                              }%`,
                            }}
                          ></div>
                        </div>
                        <small className="gas-progress-bar-label">
                          {((stats?.gas_used / stats?.gas_limit) * 100).toFixed(
                            2
                          )}
                          %
                        </small>
                      </div>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>
            <div className="bs-list-row">
              <div className="bs-list-title">
                <BTooltip
                  text={t(
                    "Cost per unit of gas spent for the transaction, in Ether and Gwei."
                  )}
                >
                  <i className="bi bi-question-circle me-2"></i>
                </BTooltip>
                {t("Gas Price")}:
              </div>
              <div className="bs-list-content">
                {loading ? (
                  <BSkeleton />
                ) : (
                  <React.Fragment>
                    {toGwei(stats?.gas_price)} Gwei (
                    {formatUnit(toXDCNumber(stats?.gas_price, 18, 18))})
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {stats?.block && (
        <React.Fragment>
          <div className="bs-card mb-3">
            <div className="bs-card-body py-3 px-4">
              <div className="bs-list">
                {showMore && (
                  <React.Fragment>
                    <div className="bs-list-row">
                      <div className="bs-list-title">
                        <BTooltip
                          text={t(
                            "Maximum amount of gas allocated for the transaction & the amount eventually used. Normal ETH transfers involve 21,000 gas units while contracts involve higher values."
                          )}
                        >
                          <i className="bi bi-question-circle me-2"></i>
                        </BTooltip>
                        {t("Gas Limit & Usage by Txn")}:
                      </div>
                      <div className="bs-list-content">
                        {loading ? (
                          <BSkeleton />
                        ) : (
                          <React.Fragment>
                            {formatNumber(stats?.gas_limit)} |
                            {formatNumber(stats?.gas_used || 0)} (
                            {formatPercent(
                              stats?.gas_used || 0,
                              stats?.gas_limit
                            )}
                            %)
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                    {stats?.type == 2 && (
                      <div className="bs-list-row">
                        <div className="bs-list-title">
                          <BTooltip
                            text={t(
                              "Base Fee refers to the network Base Fee at the time of the block, while Max Fee & Max Priority Fee refer to the max amount a user is willing to pay for their tx & to give to the block producer respectively."
                            )}
                          >
                            <i className="bi bi-question-circle me-2"></i>
                          </BTooltip>
                          {t("Gas Fees")}:
                        </div>
                        <div className="bs-list-content">
                          {loading ? (
                            <BSkeleton />
                          ) : (
                            <React.Fragment>
                              {t("Base")}: {toGwei(stats?.base_fee_per_gas)}{" "}
                              Gwei &nbsp;
                              {stats?.type == 2 && (
                                <React.Fragment>
                                  | {t("Max")}: {toGwei(stats?.max_fee_per_gas)}{" "}
                                  Gwei | {t("Max Priority")}:{" "}
                                  {toGwei(stats?.max_priority_fee_per_gas)} Gwei
                                </React.Fragment>
                              )}
                            </React.Fragment>
                          )}
                        </div>
                      </div>
                    )}
                    {stats?.type == 2 && (
                      <React.Fragment>
                        <div className="bs-list-row">
                          <div className="bs-list-title">
                            <BTooltip
                              text={t(
                                "Total amount of ETH burnt from this tx & total fees saved from the amount the user was willing to pay for this tx."
                              )}
                            >
                              <i className="bi bi-question-circle me-2"></i>
                            </BTooltip>
                            {t("Burnt & Txn Savings Fees")}:
                          </div>
                          <div className="bs-list-content">
                            {loading ? (
                              <BSkeleton />
                            ) : (
                              <React.Fragment>
                                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                  <span className="bs-font-secondary">
                                    🔥 {t("Burnt")}:
                                  </span>
                                  {formatUnit(
                                    toXDCNumber(stats?.tx_burnt_fee ?? 0, 18)
                                  )}
                                </span>
                                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                  <span className="bs-font-secondary">
                                    💸 {t("Txn Savings")}:
                                  </span>
                                  {/* {formatUnit(
                                toXDCNumber(
                                  stats?.max_priority_fee_per_gas ?? 0,
                                  18
                                )
                              )} */}
                                  {calculateTransactionFeeSavings(
                                    stats?.max_fee_per_gas,
                                    stats?.base_fee_per_gas || 0,
                                    stats?.max_priority_fee_per_gas,
                                    stats?.gas_used
                                  )}{" "}
                                  {process.env.REACT_APP_BASE_UNIT}
                                </span>
                              </React.Fragment>
                            )}
                          </div>
                        </div>
                        <hr />
                      </React.Fragment>
                    )}
                    <div className="bs-list-row">
                      <div className="bs-list-title">
                        <BTooltip
                          text={t("Other data related to this transaction.")}
                        >
                          <i className="bi bi-question-circle me-2"></i>
                        </BTooltip>
                        {t("Other Attributes")}:
                      </div>
                      <div className="bs-list-content">
                        {loading ? (
                          <BSkeleton />
                        ) : (
                          <React.Fragment>
                            {stats?.type == 2 ? (
                              <React.Fragment>
                                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                  <span className="bs-font-secondary">
                                    {t("Txn Type")}:
                                  </span>
                                  {stats?.type} &nbsp;
                                  <span className="bs-font-secondary">
                                    (EIP-1559)
                                  </span>
                                </span>
                                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                  {t("Nonce")}: {stats?.nonce}
                                </span>
                                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                  <span className="bs-font-secondary">
                                    {t("Position")}: {stats?.position}
                                  </span>
                                </span>
                              </React.Fragment>
                            ) : (
                              <React.Fragment>
                                {stats?.type == 0 ? (
                                  <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                    {t("Txn Type")}:{" "}
                                    <span className="fw-bold">
                                      0 ({t("Legacy")}){" "}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                    {t("Txn Type")}:{" "}
                                    <span className="fw-bold">
                                      {stats?.type}{" "}
                                    </span>
                                  </span>
                                )}
                                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                  {t("Nonce")}:
                                  <span className="fw-bold">
                                    {" "}
                                    {stats?.nonce}
                                  </span>{" "}
                                </span>
                                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap  px-2 me-2">
                                  {t("Position")}:{" "}
                                  <span className="fw-bold">
                                    {stats?.index}{" "}
                                  </span>{" "}
                                </span>
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                    <div className="bs-list-row">
                      <div className="bs-list-title">
                        <BTooltip
                          text={t(
                            "Number of the block in which the transaction is recorded. Block confirmations indicate how many blocks have been added since the transaction was produced."
                          )}
                        >
                          <i className="bi bi-question-circle me-2"></i>
                        </BTooltip>
                        {t("Input Data")}:
                      </div>

                      <div className="bs-list-content decode-item-content">
                        {!stats.decoded_input && stats?.raw_input && (
                          <React.Fragment>
                            <div className="d-flex align-items-center raw-input-content">
                              <div className="bs-log-margin">
                                <div className="bs-log-arrow">
                                  <select
                                    aria-label="Data type"
                                    onChange={(event) => handleSelect(event)}
                                    className="bs-log-dropdown bs-list-content"
                                  >
                                    <option value="hex">{t("Hex")}</option>
                                    <option value="address">UTF-8</option>
                                  </select>

                                  <div className="bs-log-dropdown-arrow">
                                    <svg
                                      viewBox="0 0 24 24"
                                      role="presentation"
                                      aria-hidden="true"
                                      style={{
                                        width: "1em",
                                        height: "1em",
                                        color: "currentColor",
                                      }}
                                    >
                                      <path
                                        fill="currentColor"
                                        d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
                                      ></path>
                                    </svg>
                                  </div>
                                </div>
                              </div>
                              <div className="bs-log-list-topic bg-light font-monospace">
                                <div className="bs-list">
                                  <span className="bs-list-content decode-text text-muted">
                                    {isMobile
                                      ? centerTruncate(
                                          selectedInputType === "address"
                                            ? hexToUtf8(stats?.raw_input)
                                            : stats?.raw_input
                                        )
                                      : selectedInputType === "address"
                                      ? hexToUtf8(stats?.raw_input)
                                      : stats?.raw_input}
                                  </span>
                                  <BCopy
                                    value={stats?.raw_input}
                                    tooltip={`${t("to clipboard")}`}
                                  />
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        )}

                        {stats?.decoded_input && (
                          <React.Fragment>
                            <BTooltip
                              text={t(
                                "The binary data that formed the input to the transaction, either the input data if it was a message call or the contract initialisation if it was a contract creation"
                              )}
                            >
                              <div className="bs-tab-item decoded-tab-item p-3">
                                {decodeType === "default" && (
                                  <div className="bs-font-secondary decode-text font-monospace">
                                    {stats?.decoded_input?.method_call ? (
                                      <React.Fragment>
                                        <p className="mb-3">
                                          Function:{" "}
                                          {stats?.decoded_input?.method_call}
                                        </p>
                                        <p>
                                          MethodId:{" "}
                                          {stats?.decoded_input?.method_id}
                                        </p>
                                      </React.Fragment>
                                    ) : (
                                      <textarea
                                        readOnly
                                        spellCheck="false"
                                        className="form-control bg-light text-muted font-monospace border-0 shadow-none custom-textarea-transaction"
                                        rows={3}
                                      >
                                        {stats?.raw_input || "0x"}
                                      </textarea>
                                    )}
                                    {stats?.decoded_input?.parameters?.length >
                                      0 &&
                                      stats?.decoded_input?.parameters?.map(
                                        (ele: any, index: number) => {
                                          // if (ele?.type === "address") {
                                          return (
                                            <div className="d-flex">
                                              <p className="me-2">[{index}]:</p>
                                              <p>{ele?.value}</p>
                                            </div>
                                          );
                                          // }
                                        }
                                      )}
                                  </div>
                                )}
                                {decodeType === "decodeInput" && (
                                  <div
                                    className="scrollbox scrollbar-custom"
                                    style={{
                                      margin: "-16px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <div id="inputDecode">
                                      {stats?.decoded_input?.parameters
                                        ?.length > 0 ? (
                                        <table
                                          className="table table-sm table-hover decoded-table p-2"
                                          style={{
                                            marginBottom: "0",
                                            backgroundColor: "#f0f0f0",
                                          }}
                                        >
                                          <thead className="decode-table-head">
                                            <tr>
                                              <th
                                                className="px-3"
                                                style={{ width: "5%" }}
                                              >
                                                <strong>#</strong>
                                              </th>
                                              <th>
                                                <strong>{t("Name")}</strong>
                                              </th>
                                              <th>
                                                <strong>{t("Type")}</strong>
                                              </th>
                                              <th>
                                                <strong>{t("Data")}</strong>
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {stats?.decoded_input?.parameters?.map(
                                              (
                                                para: any,
                                                paraindex: number
                                              ) => (
                                                <React.Fragment>
                                                  <tr
                                                    key={paraindex}
                                                    className={
                                                      paraindex <
                                                      stats?.decoded_input
                                                        ?.parameters?.length -
                                                        1
                                                        ? "decode-tr-div"
                                                        : "decode-tr-div"
                                                    }
                                                  >
                                                    <td className="px-3">
                                                      {paraindex}
                                                    </td>
                                                    <td
                                                      className={
                                                        paraindex <
                                                        stats?.decoded_input
                                                          ?.parameters?.length -
                                                          1
                                                          ? "bs-log-decode-table-cell bs-log-tbl-row text-truncate"
                                                          : "bs-log-decode-table-cell text-truncate"
                                                      }
                                                    >
                                                      {para?.name}
                                                    </td>
                                                    <td
                                                      className={
                                                        paraindex <
                                                        stats?.decoded_input
                                                          ?.parameters?.length -
                                                          1
                                                          ? "bs-log-decode-table-cell bs-log-tbl-row text-truncate"
                                                          : "bs-log-decode-table-cell text-truncate"
                                                      }
                                                    >
                                                      {para?.type}
                                                    </td>
                                                    <td className="bs-log-decode-table-cell bs-log-table-cell-data d-flex">
                                                      <div>
                                                        <BTooltip
                                                          text={para?.value}
                                                        >
                                                          {para?.type ==
                                                          "address" ? (
                                                            <React.Fragment>
                                                              <Link
                                                                to={`/address/${get0x(
                                                                  para?.value
                                                                )}`}
                                                              >
                                                                {isMobile
                                                                  ? centerTruncate(
                                                                      para?.value
                                                                    )
                                                                  : para?.value}
                                                              </Link>
                                                            </React.Fragment>
                                                          ) : isMobile ? (
                                                            centerTruncate(
                                                              para?.value
                                                            )
                                                          ) : (
                                                            para?.value
                                                          )}
                                                          {para?.type ==
                                                            "address" && (
                                                            <BCopy
                                                              value={
                                                                para?.value
                                                              }
                                                              tooltip="Logdata to clipboard"
                                                            />
                                                          )}
                                                        </BTooltip>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                </React.Fragment>
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      ) : (
                                        <p className="p-3">
                                          Unable to decoded input data
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {decodeType === "utf8" && (
                                  <div>
                                    {isMobile
                                      ? centerTruncate(
                                          hexToUtf8(stats?.raw_input)
                                        )
                                      : hexToUtf8(stats?.raw_input)}
                                  </div>
                                )}
                                {decodeType === "original" && (
                                  <textarea
                                    readOnly
                                    spellCheck="false"
                                    className="form-control bg-light text-muted font-monospace border-0 shadow-none custom-textarea-transaction"
                                    rows={3}
                                  >
                                    {stats?.raw_input}
                                  </textarea>
                                )}
                              </div>
                            </BTooltip>
                            {stats?.raw_input && (
                              <>
                                {decodeType === "decodeInput" ? (
                                  <div className="bs-tab">
                                    <button
                                      className="bs-tab-item"
                                      onClick={() => setDecodeType("default")}
                                    >
                                      <i className="bi bi-arrow-clockwise me-2"></i>
                                      Switch back
                                    </button>
                                  </div>
                                ) : (
                                  <div className="bs-tab d-flex">
                                    <div className="dropdown">
                                      <button
                                        className="btn bs-tab-item dropdown-toggle transaction-input-data-button"
                                        type="button"
                                        id="dropdownMenuButton2"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                      >
                                        View Input As
                                      </button>
                                      <div
                                        className="dropdown-menu p-2 dropdown-menu-bg-color"
                                        aria-labelledby="dropdownMenuButton2"
                                        style={{ minWidth: "10rem" }}
                                      >
                                        <p
                                          className={`dropdown-item rounded ${
                                            decodeType === "default"
                                              ? "active-dropdown-item"
                                              : "dropdown-item-hover-div"
                                          }`}
                                          onClick={() =>
                                            setDecodeType("default")
                                          }
                                          style={{ cursor: "pointer" }}
                                        >
                                          Default View
                                        </p>
                                        <p
                                          className={`dropdown-item rounded ${
                                            decodeType === "utf8"
                                              ? "active-dropdown-item"
                                              : "dropdown-item-hover-div"
                                          }`}
                                          onClick={() => setDecodeType("utf8")}
                                          style={{ cursor: "pointer" }}
                                        >
                                          UTF-8
                                        </p>
                                        <p
                                          className={`dropdown-item rounded ${
                                            decodeType === "original"
                                              ? "active-dropdown-item"
                                              : "dropdown-item-hover-div"
                                          }`}
                                          onClick={() =>
                                            setDecodeType("original")
                                          }
                                          style={{ cursor: "pointer" }}
                                        >
                                          Original
                                        </p>
                                      </div>
                                    </div>
                                    {/* <button
                                                            className="bs-tab-item"
                                                            onClick={() => setDecodeType("decodeInput")}
                                                        >
                                                            <i className="bi bi-database-fill-gear me-1"></i>
                                                            Decoded Input Data
                                                        </button> */}
                                    <Link to={`/advanced-filter`}>
                                      <button className="bs-tab-item">
                                        <i className="bi bi-funnel me-1"></i>
                                        Advanced Filter
                                      </button>
                                    </Link>
                                  </div>
                                )}
                              </>
                            )}
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                    <hr />
                  </React.Fragment>
                )}
                <div className="bs-list-row">
                  <div className="bs-list-title">{t("More Details")}:</div>
                  <div className="bs-list-content">
                    <a onClick={() => setShowMore(!showMore)} className="bs-cp">
                      {showMore
                        ? `- ${t("Click to show less")}`
                        : `+ ${t("Click to show more")}`}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default TransactionDetailsOverview;
